
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Database, Trash2, HardDrive, AlertTriangle } from 'lucide-react';

interface OrphanedStats {
  totalFiles: number;
  orphanedFiles: number;
  orphanedPercentage: number;
  totalSizeMB: number;
  orphanedSizeMB: number;
  orphanedSizePercentage: number;
}

interface OrphanedMediaStatsProps {
  stats: OrphanedStats;
  isLoading: boolean;
}

const OrphanedMediaStats: React.FC<OrphanedMediaStatsProps> = ({ stats, isLoading }) => {
  const formatMB = (mb: number) => {
    if (mb < 1) return `${(mb * 1024).toFixed(1)} KB`;
    if (mb > 1024) return `${(mb / 1024).toFixed(1)} GB`;
    return `${mb.toFixed(1)} MB`;
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-24 bg-white/5 rounded-lg animate-pulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
            <Database className="h-4 w-4" />
            Total de Arquivos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-white">{stats.totalFiles}</div>
          <p className="text-xs text-white/60">
            {formatMB(stats.totalSizeMB)} total
          </p>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-400" />
            Arquivos Órfãos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-yellow-400">{stats.orphanedFiles}</div>
          <p className="text-xs text-white/60">
            {stats.orphanedPercentage.toFixed(1)}% do total
          </p>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
            <HardDrive className="h-4 w-4 text-red-400" />
            Espaço Desperdiçado
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-400">
            {formatMB(stats.orphanedSizeMB)}
          </div>
          <p className="text-xs text-white/60">
            {stats.orphanedSizePercentage.toFixed(1)}% do espaço
          </p>
        </CardContent>
      </Card>

      <Card className="bg-white/5 border-white/10">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
            <Trash2 className="h-4 w-4 text-green-400" />
            Economia Potencial
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-green-400">
            {formatMB(stats.orphanedSizeMB)}
          </div>
          <p className="text-xs text-white/60">
            {stats.orphanedFiles} arquivo{stats.orphanedFiles !== 1 ? 's' : ''}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrphanedMediaStats;
